import { createSelector } from "@reduxjs/toolkit";
import { createAppSlice } from "../../../store/createAppSlice";
import type { PromptFormValues } from "../forms/PromptForm/PromptFormSchema";

interface PromptFormState {
  formData: Record<string, PromptFormValues>; // Key by prompt ID (tab.id)
  isDirty: Record<string, boolean>; // Track which forms have unsaved changes
}

const initialState: PromptFormState = {
  formData: {},
  isDirty: {},
};

const promptFormSlice = createAppSlice({
  name: "promptForm",
  initialState,
  reducers: (create) => ({
    saveFormState: create.reducer<{
      id: string;
      formData: PromptFormValues;
      isDirty: boolean;
    }>((state, action) => {
      const { id, formData, isDirty } = action.payload;

      // We need to deep copy the form data to avoid mutating the original object
      // in the react-hook-form. See: https://github.com/orgs/react-hook-form/discussions/3715
      const formDataDeepCopy = JSON.parse(JSON.stringify(formData));

      state.formData[id] = formDataDeepCopy;
      state.isDirty[id] = isDirty;
    }),

    // TODO: Shall "markFormClean" be described as "savedToServer"?
    markFormClean: create.reducer<string>((state, action) => {
      const id = action.payload;
      if (state.isDirty[id]) {
        state.isDirty[id] = false;
      }
    }),

    // TODO: Shall "markFormDirty" be described as "savedToServer"?
    markFormDirty: create.reducer<string>((state, action) => {
      const id = action.payload;
      if (!state.isDirty[id]) {
        state.isDirty[id] = true;
      }
    }),

    // Reset form state when a tab is closed
    resetFormState: create.reducer<string[]>((state, action) => {
      const tabIds = action.payload;

      tabIds.forEach((id) => {
        delete state.formData[id];
        delete state.isDirty[id];
      });
    }),
  }),

  selectors: {
    // Get form state for a specific prompt
    getFormState: (state, promptId: string) => state.formData[promptId],

    // Check if a form has unsaved changes
    getIsDirty: (state, promptId: string) => !!state.isDirty[promptId],

    // Get all prompt IDs with unsaved changes
    getDirtyForms: createSelector(
      (state: PromptFormState) => state.isDirty,
      (isDirty) => Object.keys(isDirty).filter((id) => isDirty[id]),
    ),
  },
});

const { actions, selectors } = promptFormSlice;

export { actions, selectors, promptFormSlice };
